/* Clickmore theme: site behaviour. No dependencies.
   Each feature initialises inside a root element so it also works when a
   section is re-rendered in the Shopify theme editor. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const BURST = 'M22 11 L22 5 M17.5 12.5 L13.3 8.3 M16 17 L10 17';
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  if (designMode) document.documentElement.classList.add('shopify-design-mode');

  /* ---------- Header border once the page scrolls ---------- */
  const onScroll = () => {
    const header = document.querySelector('[data-header]');
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 8);
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  /* ---------- Mobile menu ---------- */
  function initMenu(root) {
    const toggle = root.querySelector('.menu-toggle');
    const nav = root.querySelector('.site-nav');
    if (!toggle || !nav || toggle.dataset.ready) return;
    toggle.dataset.ready = '1';
    const label = toggle.querySelector('.sr-only');
    const setOpen = (open) => {
      toggle.setAttribute('aria-expanded', String(open));
      if (label) label.textContent = open ? 'Close menu' : 'Menu';
      document.documentElement.classList.toggle('menu-open', open);
    };
    toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));
    nav.addEventListener('click', (e) => { if (e.target.closest('a')) setOpen(false); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') { setOpen(false); toggle.focus(); }
    });
    window.matchMedia('(min-width: 900px)').addEventListener('change', (e) => { if (e.matches) setOpen(false); });
  }

  /* ---------- Dropdowns in the menu (Services) ---------- */
  const desktop = window.matchMedia('(min-width: 900px)');
  function initSubmenus(root) {
    root.querySelectorAll('[data-sub]').forEach((item) => {
      const btn = item.querySelector('.nav-sub-toggle');
      if (!btn || btn.dataset.ready) return;
      btn.dataset.ready = '1';
      const label = btn.querySelector('.sr-only');
      const name = label ? label.textContent.replace(/^Show /, '') : '';
      const setOpen = (open) => {
        btn.setAttribute('aria-expanded', String(open));
        item.classList.toggle('is-open', open);
        if (label) label.textContent = (open ? 'Hide ' : 'Show ') + name;
      };
      btn.addEventListener('click', () => setOpen(btn.getAttribute('aria-expanded') !== 'true'));
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && item.classList.contains('is-open')) { e.stopPropagation(); setOpen(false); btn.focus(); }
      });
      item.addEventListener('focusout', (e) => { if (desktop.matches && !item.contains(e.relatedTarget)) setOpen(false); });
      document.addEventListener('click', (e) => { if (desktop.matches && !item.contains(e.target)) setOpen(false); });
      desktop.addEventListener('change', () => setOpen(false));
    });
  }

  /* ---------- Reveal on scroll ---------- */
  const io = 'IntersectionObserver' in window && !designMode
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
        });
      }, { rootMargin: '0px 0px -40px 0px', threshold: 0 }) // start as soon as an element is 40px into view
    : null;
  function initReveal(root) {
    root.querySelectorAll('[data-reveal], .steps').forEach((el) => {
      if (io) io.observe(el); else el.classList.add('is-in');
    });
  }

  /* ---------- Cursor-follow highlight on cards ---------- */
  function initSpot(root) {
    root.querySelectorAll('.spot').forEach((el) => {
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${e.clientX - r.left}px`);
        el.style.setProperty('--my', `${e.clientY - r.top}px`);
      });
    });
  }

  /* ---------- Signature: every click fires the logo's burst lines ---------- */
  const SVG_NS = 'http://www.w3.org/2000/svg';
  document.addEventListener('pointerdown', (e) => {
    if (reduceMotion.matches || designMode) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (e.target.closest('input, textarea, select')) return;
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 48 48');
    svg.setAttribute('aria-hidden', 'true');
    svg.classList.add('click-burst');
    if (e.target.closest('.cta-band, .btn-primary')) svg.classList.add('on-accent');
    else if (e.target.closest('.bg-ink, .site-footer, .compare-new')) svg.classList.add('on-dark');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', BURST);
    svg.appendChild(path);
    svg.style.left = `${e.clientX}px`;
    svg.style.top = `${e.clientY}px`;
    document.body.appendChild(svg);
    setTimeout(() => svg.remove(), 700);
  }, { passive: true });

  /* ---------- Hero demo: a cursor clicking example ads ---------- */
  function initHero(root) {
    const art = root.querySelector('.hero-art');
    if (!art || art.dataset.ready) return;
    art.dataset.ready = '1';
    const cursor = art.querySelector('.demo-cursor');
    const [socialBtn, searchTitle] = art.querySelectorAll('[data-demo-target]');
    const query = art.querySelector('.search-query');
    if (!cursor || !socialBtn || !searchTitle || !query) return;
    let queries = [];
    try { queries = JSON.parse(query.dataset.queries || '[]').filter(Boolean); } catch (_) { /* bad data */ }
    if (!queries.length) queries = [query.textContent];

    let visible = true;
    let wake = null;
    const resume = () => { if (wake) { wake(); wake = null; } };
    new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; if (visible) resume(); }).observe(art);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) resume(); });
    const whenVisible = () => (visible && !document.hidden && art.isConnected) ? Promise.resolve() : new Promise((r) => { wake = r; });

    const tip = () => {
      const w = cursor.getBoundingClientRect().width;
      return { x: (w * 22) / 48, y: (w * 17) / 48 };
    };
    const pointOn = (el, fx, fy) => {
      const a = art.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      return { x: r.left - a.left + r.width * fx, y: r.top - a.top + r.height * fy };
    };
    const toTransform = (p, t) => `translate(${(p.x - t.x).toFixed(1)}px, ${(p.y - t.y).toFixed(1)}px)`;
    let pos = null;
    const place = (p) => { pos = p; cursor.style.transform = toTransform(p, tip()); };
    const moveTo = async (to, duration) => {
      const from = pos;
      const t = tip();
      const dx = to.x - from.x, dy = to.y - from.y;
      const ctrl = { x: from.x + dx / 2 + dy * 0.25, y: from.y + dy / 2 - dx * 0.25 };
      const frames = [];
      for (let i = 0; i <= 20; i++) {
        const s = i / 20, u = 1 - s;
        frames.push({ transform: toTransform({
          x: u * u * from.x + 2 * u * s * ctrl.x + s * s * to.x,
          y: u * u * from.y + 2 * u * s * ctrl.y + s * s * to.y,
        }, t) });
      }
      const anim = cursor.animate(frames, { duration, easing: 'cubic-bezier(.65,0,.35,1)', fill: 'forwards' });
      await anim.finished;
      place(to);
      anim.cancel();
    };
    const click = async (el) => {
      cursor.classList.remove('is-clicking');
      void cursor.getBoundingClientRect();
      cursor.classList.add('is-clicking');
      el.classList.add('is-pressed');
      await sleep(170);
      el.classList.remove('is-pressed');
      await sleep(560);
      cursor.classList.remove('is-clicking');
    };
    const typeQuery = async (text) => {
      while (query.textContent.length) { query.textContent = query.textContent.slice(0, -1); await sleep(18); }
      for (const ch of text) { query.textContent += ch; await sleep(45 + Math.random() * 55); }
    };

    if (reduceMotion.matches) {
      requestAnimationFrame(() => { place(pointOn(socialBtn, 0.55, 0.6)); cursor.classList.add('is-ready'); });
      return;
    }
    (async () => {
      await sleep(1100);
      place(pointOn(art, 0.62, 0.52));
      cursor.classList.add('is-ready');
      await sleep(400);
      let i = 0;
      while (art.isConnected) {
        await whenVisible();
        await moveTo(pointOn(searchTitle, 0.42, 0.62), 1050);
        await click(searchTitle);
        await sleep(650);
        await whenVisible();
        await moveTo(pointOn(socialBtn, 0.5, 0.62), 1150);
        await click(socialBtn);
        await sleep(500);
        i = (i + 1) % queries.length;
        await typeQuery(queries[i]);
        await sleep(450);
      }
    })();
  }

  /* ---------- Buddy: give the office dog a pat ---------- */
  const reactions = [
    'Good boy levels: critical.',
    'Tail speed: maximum.',
    'Buddy would like to know if you have any snacks.',
    "That's the spot. Don't stop.",
    'Buddy has officially approved this website.',
    "He's rolled over. You're friends now.",
    'Buddy now expects this every time you visit.',
    'Ten out of ten. Would be patted again.',
    "He's gone to fetch his lead. Sorry, that's not how websites work.",
  ];
  let pats = 0;
  try { pats = parseInt(localStorage.getItem('cm-buddy-pats'), 10) || 0; } catch (_) { /* storage blocked */ }
  let lastReaction = -1;
  function initPat(root) {
    root.querySelectorAll('[data-pat-wrap]').forEach((wrap) => {
      const btn = wrap.querySelector('[data-pat]');
      const card = wrap.querySelector('[data-pat-card]');
      const msg = wrap.querySelector('[data-pat-msg]');
      if (!btn || !card || !msg || btn.dataset.ready) return;
      btn.dataset.ready = '1';
      const pat = () => {
        pats += 1;
        try { localStorage.setItem('cm-buddy-pats', String(pats)); } catch (_) { /* storage blocked */ }
        let i;
        do { i = Math.floor(Math.random() * reactions.length); } while (i === lastReaction);
        lastReaction = i;
        const count = document.createElement('strong');
        count.textContent = `Pats from you: ${pats}`;
        msg.replaceChildren(document.createTextNode(`${reactions[i]} `), count);
        if (reduceMotion.matches) return;
        card.classList.remove('is-patted');
        void card.offsetWidth;
        card.classList.add('is-patted');
        ['❤️', '🦴', '🐾', '💙', '❤️'].forEach((emoji, n) => {
          const pop = document.createElement('span');
          pop.className = 'pat-pop';
          pop.setAttribute('aria-hidden', 'true');
          pop.textContent = emoji;
          pop.style.left = `${25 + Math.random() * 50}%`;
          pop.style.setProperty('--dx', `${(Math.random() - 0.5) * 90}px`);
          pop.style.animationDelay = `${n * 80}ms`;
          card.appendChild(pop);
          setTimeout(() => pop.remove(), 1800);
        });
      };
      btn.addEventListener('click', pat);
      card.addEventListener('click', pat);
    });
  }

  /* ---------- Cookie settings: reopen Shopify's own consent banner ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cookie-settings]');
    if (!btn) return;
    if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
      e.preventDefault();
      window.privacyBanner.showPreferences();
    }
  });

  /* ---------- Enquiries: copy to HubSpot once Shopify has accepted them ---------- */
  // On submit the details are kept for this tab only. They're sent to HubSpot from the
  // "Thanks" page, so anything stopped by Shopify's spam check never reaches the CRM.
  const HS_KEY = 'clickmore-enquiry';
  const hsConfig = (() => {
    try { return JSON.parse(document.getElementById('hubspot-config').textContent); } catch (_) { return null; }
  })();

  function hubspotFields(form) {
    const data = new FormData(form);
    const get = (name) => String(data.get(`contact[${name}]`) || '').trim();
    const [first, ...rest] = get('name').split(/\s+/);
    const interests = [];
    data.forEach((value, key) => {
      const match = key.match(/^contact\[Interested in: (.+)\]$/);
      if (match && value) interests.push(match[1]);
    });
    let website = get('Website');
    if (website && !/^https?:\/\//i.test(website)) website = `https://${website}`;
    const fields = {
      firstname: first || '',
      lastname: rest.join(' '),
      email: get('email'),
      phone: get('Phone'),
      company: get('Business or brand'),
      website,
      message: get('body'),
      enquiry_type: get('Enquiry type') || 'Contact form',
      services_interested_in: interests.join('; '),
      monthly_budget: get('Monthly budget'),
      best_time_to_call: [get('Best day'), get('Best time')].filter(Boolean).join(', '),
    };
    return Object.entries(fields).filter(([, value]) => value).map(([name, value]) => ({ name, value }));
  }

  if (hsConfig) {
    document.querySelectorAll('#enquiry-form, #call-request-form').forEach((form) => {
      form.addEventListener('submit', () => {
        try {
          sessionStorage.setItem(HS_KEY, JSON.stringify({
            fields: hubspotFields(form), pageUri: location.origin + location.pathname, pageName: document.title, at: Date.now(),
          }));
        } catch (_) { /* storage blocked: the email still arrives */ }
      });
    });
  }

  if (document.querySelector('[data-enquiry-success]')) {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(HS_KEY) || 'null');
      sessionStorage.removeItem(HS_KEY);
    } catch (_) { /* nothing saved */ }
    if (hsConfig && saved && saved.fields && saved.fields.length && Date.now() - saved.at < 30 * 60 * 1000) {
      fetch(`https://${hsConfig.host}/submissions/v3/integration/submit/${encodeURIComponent(hsConfig.portal)}/${encodeURIComponent(hsConfig.form)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({ fields: saved.fields, context: { pageUri: saved.pageUri, pageName: saved.pageName } }),
      }).catch(() => { /* the email still arrives */ });
    }

    /* Tell Shopify's customer events (GA4 and Meta pixels) */
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish('enquiry_submitted', { form: 'contact' });
    }
  }

  /* ---------- Booking calendar: only load Google's calendar when asked ---------- */
  function initBooking(root) {
    root.querySelectorAll('[data-booking]').forEach((box) => {
      const btn = box.querySelector('[data-booking-load]');
      const tpl = box.querySelector('template[data-booking-frame]');
      if (!btn || !tpl || btn.dataset.ready) return;
      btn.dataset.ready = '1';
      btn.addEventListener('click', () => {
        box.classList.add('is-loaded');
        box.replaceChildren(tpl.content.cloneNode(true));
        const frame = box.querySelector('iframe');
        if (frame) frame.focus();
      });
    });
  }

  /* ---------- Contact email: rebuilt here, so the address never appears in the page source for spam bots ---------- */
  function initEmail(root) {
    const flip = (s) => (s || '').split('').reverse().join('');
    root.querySelectorAll('[data-email-user]').forEach((link) => {
      const address = `${flip(link.dataset.emailUser)}@${flip(link.dataset.emailDomain)}`;
      link.href = `mailto:${address}`;
      link.textContent = address;
      delete link.dataset.emailUser;
    });
  }

  const initAll = (root) => { initMenu(root); initSubmenus(root); initReveal(root); initSpot(root); initHero(root); initPat(root); initBooking(root); initEmail(root); };
  initAll(document);
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
